import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import clsx from 'clsx';
import { renderToString } from 'react-dom/server';
import { BeatLoader } from 'react-spinners';

import SharingPopup from 'components/SharingPopup';

import DataHandler from 'config/DataHandler';

import { imgixUrl, oldImgixUrl } from 'settings';

const imgix = (appId) => (appId > 600 ? imgixUrl : oldImgixUrl);

const parseParameters = (params) =>
  Object.keys(params)
    .map((key, i) => `${i === 0 ? '?' : '&'}${key}=${params[key]}`)
    .join('');

const parseFancyboxId = (src) => {
  if (!src?.groups) {
    return src;
  }
  const { groups } = /.+\/(?<name>.+)\.(?:.*?)$/g.exec(src);
  if (groups) return groups.name;
  return src;
};

const Gradient = ({ id = null, className = null }) => (
  <div id={id} className={clsx('overlay', 'gradient', { [className]: className })} />
);

const getSize = ({ loadWrapper, imageElement, default: { height, width } }, callback) => {
  const { current: loadRef } = loadWrapper;
  const { current: imageRef } = imageElement;

  const { clientWidth, clientHeight } = loadRef || imageRef;

  const size = {
    width: Math.max(Math.floor(clientWidth), width),
    height: Math.max(Math.floor(clientHeight), height),
  };

  callback(size);
};

const getImageURL = async (src, { height: h, width: w }, callback) => {
  // NOTE: Removes double slashes and parameters
  const [protocol] = src.match(/^(?<protocol>https?:\/\/)/g);
  const cleanSrc = [
    protocol,
    src
      .replace(protocol, '')
      .replace(/\/\//g, '/')
      .replace(/\?.*?$/g, ''),
  ].join('');

  const {
    groups: { imagePath, appId = DataHandler.matchAppId() },
  } = /(?<appId>[0-9]{4})\/(?<imagePath>.*?)$/g.exec(cleanSrc) || { groups: {} };
  const params = parseParameters({ fit: 'crop', w, h });

  // NOTE: window needs to be specified because this class is named Image as well
  const img = new window.Image();
  
  // iOS Safari compatibility fixes
  img.crossOrigin = 'anonymous';
  img.loading = 'eager';
  
  img.onload = () => {
    callback({ image: img.src, loading: false });
  };
  img.onerror = () => {
    callback({ image: cleanSrc, loading: false });
  };

  const url = imgix(appId);

  if (imagePath && src.includes('http')) {
    img.src = `${url}/${appId}/${imagePath}${params}`;
  }
  if (!imagePath || !src.includes('http')) {
    img.src = `${url}/${appId}/${cleanSrc}${params}`;
  }
};

const defaultFancyOptions = {
  infobar: true,
  thumbs: {
    autoStart: true,
    axis: 'y',
  },
  buttons: ['share', 'close'],
};

const Image = ({
  default: { height = 0, width = 0 } = {},
  alt = '',
  // appId = -1,
  caption = '',
  children = null,
  className = null,
  fancybox = null,
  gradient = false,
  load = () => {},
  src: srcProp,
}) => {
  const [image, setImage] = useState('https://via.placeholder.com/100x100/f5f5f5/f5f5f5');
  const [size, setSize] = useState({ height, width });
  const [loading, setLoading] = useState(true);

  const loadWrapper = useRef(null);
  const imageElement = useRef(null);

  // quick fix to avoid src=null errors
  const src = srcProp || '';

  useEffect(() => {
    getSize({ loadWrapper, imageElement, default: { width, height } }, setSize);
  }, [height, width]);

  useEffect(() => {
    if (size.width && size.height) {
      getImageURL(src, { ...size }, ({ image: newImage, loading: newLoading }) => {
        setLoading(newLoading);
        setImage(newImage);
      });
    }
  }, [size, src]);

  const fancyboxSettings = {
    'data-fancybox': parseFancyboxId(src),
    'data-options': {
      ...defaultFancyOptions,
      caption,
      share: {
        tpl: renderToString(<SharingPopup url={src} />),
      },
    },
    'data-caption': caption,
    href: src,
    ...fancybox,
  };

  return (
    <div className={clsx('wrapper__image')} ref={loadWrapper} {...fancyboxSettings}>
      {loading && (
        <div className={clsx('container__loader')}>
          <BeatLoader loading />
        </div>
      )}
      <img
        id={src}
        src={image}
        ref={imageElement}
        alt={alt}
        className={clsx({ [className]: className })}
        style={{ 
          opacity: loading ? 0 : 1,
          WebkitTransform: 'translateZ(0)',
          transform: 'translateZ(0)',
          WebkitBackfaceVisibility: 'hidden',
          backfaceVisibility: 'hidden'
        }}
        onLoad={load}
        onError={console.error}
        crossOrigin="anonymous"
        loading="eager"
      />
      {gradient && <Gradient id={src} />}
      {children !== undefined && children}
    </div>
  );
};

export default Image;

Image.propTypes = {
  alt: PropTypes.string,
  appId: PropTypes.number,
  caption: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.array]),
  className: PropTypes.string,
  default: PropTypes.shape({
    height: PropTypes.number,
    width: PropTypes.number,
  }),
  fancybox: PropTypes.shape(),
  gradient: PropTypes.bool,
  href: PropTypes.string,
  load: PropTypes.func,
  src: PropTypes.string.isRequired,
};

Gradient.propTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
};
